import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from './Header';
import Sidebar from './Sidebar';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { currentUser, updateProfile, users } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Determine which user profile to show
  const profileUser = userId ? users.find(u => u.id === userId) : currentUser;
  const isOwnProfile = !userId || userId === currentUser?.id;
  const canEdit = isOwnProfile || currentUser?.role === 'owner';
  
  const [formData, setFormData] = useState({
    name: profileUser?.name || '',
    email: profileUser?.email || '',
    phone: profileUser?.phone || '',
    department: profileUser?.department || '',
    bio: profileUser?.bio || ''
  });

  // Get menu items based on user role and context
  const getMenuItems = () => {
    if (currentUser?.role === 'owner') {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'home' },
        { name: 'Projects', path: '/dashboard/projects', icon: 'briefcase' },
        { name: 'Employees', path: '/dashboard/employees', icon: 'users' },
        { name: 'Attendance', path: '/dashboard/attendance', icon: 'calendar-check' },
        { name: 'Reports', path: '/dashboard/reports', icon: 'chart-bar' },
      ];
    } else if (currentUser?.role === 'project_manager') {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'home' },
        { name: 'My Projects', path: '/dashboard/projects', icon: 'briefcase' },
        { name: 'Tasks', path: '/dashboard/tasks', icon: 'check-square' },
        { name: 'Team Analytics', path: '/dashboard/team-analytics', icon: 'chart-line' },
        { name: 'Messages', path: '/dashboard/messages', icon: 'message-square' },
      ];
    } else if (currentUser?.role === 'hr') {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'home' },
        { name: 'Employees', path: '/dashboard/employees', icon: 'users' },
        { name: 'Attendance', path: '/dashboard/attendance', icon: 'calendar-check' },
        { name: 'Leave Requests', path: '/dashboard/leave', icon: 'calendar-times' },
      ];
    } else {
      return [
        { name: 'Dashboard', path: '/dashboard', icon: 'home' },
        { name: 'My Tasks', path: '/dashboard/tasks', icon: 'tasks' },
        { name: 'Attendance', path: '/dashboard/attendance', icon: 'calendar-check' },
        { name: 'Leave', path: '/dashboard/leave', icon: 'calendar-times' },
      ];
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    
    setLoading(true);

    try {
      const result = await updateProfile(formData);
      if (result.success) {
        if (isOwnProfile) {
          navigate('/dashboard');
        } else {
          navigate('/dashboard/employees');
        }
      } else {
        alert(result.message);
      }
    } catch (error) {
      alert('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  if (!profileUser) {
    return (
      <div className="dashboard-layout">
        <div className="dashboard-main">
          <Header title="Profile Not Found" />
          <div className="error-page">
            <h2>User Not Found</h2>
            <p>The requested user profile could not be found.</p>
            <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar 
        menuItems={getMenuItems()}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <div className="dashboard-main">
        <Header 
          title={isOwnProfile ? 'My Profile' : `${profileUser.name}'s Profile`}
          onMenuClick={() => setSidebarOpen(true)}
        />

        <main className="dashboard-body">
          <div className="profile-page">
            <div className="profile-container">
              {/* Profile Header */}
              <div className="profile-header-section">
                <div className="profile-avatar-section">
                  <div className="profile-avatar-large">
                    {profileUser?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="profile-info">
                    <h2>{profileUser?.name}</h2>
                    <p className="role-display">{profileUser?.role?.replace('_', ' ').toUpperCase()}</p>
                    <p className="join-date">Joined: {new Date(profileUser?.joinDate).toLocaleDateString()}</p>
                    {!isOwnProfile && (
                      <div className="profile-actions">
                        <button 
                          className="btn btn-outline"
                          onClick={() => navigate('/dashboard/employees')}
                        >
                          <i className="fas fa-arrow-left"></i>
                          Back to Employees
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Profile Stats */}
                <div className="profile-stats">
  <div className="stat-item">
    <div className="stat-value">{profileUser?.id?.substring(0, 8)}</div>
    <div className="stat-label">Employee ID</div>
  </div>
  <div className="stat-item">
    <div className="stat-value">
      {profileUser?.lastPasswordChange 
        ? new Date(profileUser.lastPasswordChange).toLocaleDateString()
        : 'Never'
      }
    </div>
    <div className="stat-label">Last Password Change</div>
  </div>
  <div className="stat-item">
    <div className="stat-value">
      {/* Fix: Use proper status check with fallback */}
      {(profileUser?.status === 'active' || !profileUser?.status) ? '🟢 Active' : '🔴 Inactive'}
    </div>
    <div className="stat-label">Account Status</div>
  </div>
</div>
              </div>

              {/* Profile Form */}
              <div className="profile-form-section">
                <div className="form-header">
                  <h3>
                    {canEdit ? 'Edit Profile Information' : 'Profile Information'}
                  </h3>
                  <p>
                    {canEdit 
                      ? 'Update your personal information and preferences.'
                      : 'View profile information and details.'
                    }
                  </p>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-control"
                        required
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-control"
                        required
                        disabled={!canEdit}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="+1 (555) 123-4567"
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <select
                        name="department"
                        value={formData.department}
                        onChange={handleChange}
                        className="form-control"
                        required
                        disabled={!canEdit}
                      >
                        <option value="">Select Department</option>
                        <option value="Technology">Technology</option>
                        <option value="Human Resources">Human Resources</option>
                        <option value="Development">Development</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Sales">Sales</option>
                        <option value="Finance">Finance</option>
                        <option value="Executive">Executive</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Bio</label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Tell us about yourself..."
                      rows="4"
                      disabled={!canEdit}
                    />
                  </div>

                  {canEdit && (
                    <div className="form-actions">
                      <button 
                        type="button" 
                        className="btn btn-secondary" 
                        onClick={() => navigate(isOwnProfile ? '/dashboard' : '/dashboard/employees')}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? (
                          <>
                            <i className="fas fa-spinner fa-spin"></i>
                            Updating...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save"></i>
                            Update Profile
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProfilePage;
